import React from 'react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { PricingConfig } from './types';
import { cn } from '@/lib/utils';
import { 
  colors, 
  shadows, 
  borders, 
  spacing 
} from '@/components/business/design-system/businessDesignTokens';
import { formatPricingPreview, validatePricingConfig } from './utils/pricingUtils';
import { PricingTypeSelector } from './components/PricingTypeSelector';
import { PricingValueInput } from './components/PricingValueInput';
import { PricingModalHeader } from './components/PricingModalHeader';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  pricingConfig: {
    optionId: string;
    isCore: boolean;
    optionType: string;
    pricing: PricingConfig;
  } | null;
  setPricingConfig: (config: any | null) => void;
  onSave: () => void;
  currency: string;
  renderPricingPreview: (pricing: any) => string | null;
}

export function PricingModal({
  isOpen,
  onClose,
  pricingConfig,
  setPricingConfig,
  onSave,
  currency,
  renderPricingPreview
}: PricingModalProps) {
  
  const handleSave = () => {
    if (!pricingConfig) return;
    
    const validation = validatePricingConfig(pricingConfig.pricing);
    if (!validation.isValid) {
      // Handle validation error - could show toast here
      console.error('Validation error:', validation.error);
      return;
    }
    
    onSave();
  };

  if (!isOpen || !pricingConfig) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={cn(
            "bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto",
            shadows.level2.className,
            borders.container
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={spacing.cardPadding}>
            <PricingModalHeader onClose={onClose} />

            <div className="space-y-6">
              <PricingTypeSelector
                optionType={pricingConfig.optionType}
                isCore={pricingConfig.isCore}
                selectedType={pricingConfig.pricing.type}
                onTypeChange={(type) => 
                  setPricingConfig(pricingConfig ? {
                    ...pricingConfig,
                    pricing: { ...pricingConfig.pricing, type }
                  } : null)
                }
              />

              <PricingValueInput
                pricingType={pricingConfig.pricing.type}
                value={pricingConfig.pricing.value}
                description={pricingConfig.pricing.description}
                currency={currency}
                onValueChange={(value) => 
                  setPricingConfig(pricingConfig ? {
                    ...pricingConfig,
                    pricing: { ...pricingConfig.pricing, value }
                  } : null)
                }
                onDescriptionChange={(description) => 
                  setPricingConfig(pricingConfig ? {
                    ...pricingConfig,
                    pricing: { ...pricingConfig.pricing, description }
                  } : null)
                }
              />

              {/* Preview Section */}
              {pricingConfig.pricing.value > 0 && (
                <div className={cn(
                  "p-4 rounded-lg border",
                  `bg-${colors.success.bg}`,
                  `border-${colors.success.border}`,
                  shadows.simple.sm
                )}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-emerald-900">Preview:</span>
                    <span className="font-medium text-emerald-900">
                      {formatPricingPreview(pricingConfig.pricing, currency)}
                    </span>
                  </div>
                  <div className="text-xs text-emerald-700">
                    {pricingConfig.pricing.type === 'fixed' && 'Same amount every time'}
                    {pricingConfig.pricing.type === 'percentage' && 'Percentage of base price'}
                    {pricingConfig.pricing.type === 'per_person' && 'Multiplied by number of people'}
                    {pricingConfig.pricing.type === 'per_day' && 'Multiplied by number of days'}
                    {pricingConfig.pricing.type === 'per_person_day' && 'Multiplied by people × days'}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-200/60">
              <Button
                variant="outline"
                onClick={onClose}
                className={cn(
                  borders.interactive,
                  shadows.simple.sm,
                  "hover:shadow-md transition-shadow"
                )}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!pricingConfig.pricing.value}
                className={cn(
                  `bg-${colors.primary.main} hover:bg-${colors.primary.hover}`,
                  shadows.simple.md,
                  "hover:shadow-lg transition-shadow px-6"
                )}
              >
                Add Pricing
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
} 